import { useState, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fileUrl } from "@/lib/jobOptions";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Trash2, Check, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const MAX = 6;

/** Beheer van het fotoportfolio (max 6) voor monteurs/bedrijven. */
export function PortfolioManager() {
  const { user, reloadUser } = useAuth();
  const [photos, setPhotos] = useState(user?.portfolio || []);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const fileRef = useRef(null);

  const sync = (list) => { setPhotos(list); reloadUser(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (photos.length >= MAX) { toast.error(`Maximaal ${MAX} foto's.`); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("titel", "");
    setUploading(true);
    try {
      const { data } = await api.post("/portfolio/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      sync(data.portfolio);
      toast.success("Foto toegevoegd");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Uploaden mislukt");
    } finally {
      setUploading(false);
    }
  };

  const saveTitle = async (fileId) => {
    try {
      const { data } = await api.put(`/portfolio/photos/${fileId}`, { titel: editTitle });
      sync(data.portfolio);
      setEditing(null);
      toast.success("Titel opgeslagen");
    } catch (err) {
      toast.error("Opslaan mislukt");
    }
  };

  const remove = async (fileId) => {
    try {
      const { data } = await api.delete(`/portfolio/photos/${fileId}`);
      sync(data.portfolio);
      toast.success("Foto verwijderd");
    } catch (err) {
      toast.error("Verwijderen mislukt");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-6" data-testid="portfolio-manager">
      <div className="flex items-center justify-between mb-1">
        <p className="font-display font-bold text-slate-950 flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-orange-500" /> Portfolio
        </p>
        <span className="text-xs font-semibold text-slate-500">{photos.length}/{MAX}</span>
      </div>
      <p className="text-sm text-slate-500 mb-4">Voeg tot {MAX} foto's van je werk toe. Deze zijn zichtbaar op je profiel en in de publieke gids.</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        {photos.map((p) => (
          <div key={p.file_id} data-testid={`portfolio-item-${p.file_id}`} className="border border-slate-200 rounded-sm overflow-hidden">
            <div className="aspect-square bg-slate-50">
              <img src={fileUrl(p.file_id)} alt={p.titel || "Portfolio"} className="w-full h-full object-cover" />
            </div>
            <div className="p-2 space-y-2">
              {editing === p.file_id ? (
                <div className="flex gap-1">
                  <Input data-testid={`portfolio-title-input-${p.file_id}`} value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="Titel" className="h-8 text-xs rounded-sm" />
                  <Button data-testid={`portfolio-title-save-${p.file_id}`} size="icon" onClick={() => saveTitle(p.file_id)}
                    className="h-8 w-8 shrink-0 bg-orange-500 hover:bg-orange-600 rounded-sm">
                    <Check className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <button type="button" onClick={() => { setEditing(p.file_id); setEditTitle(p.titel || ""); }}
                  data-testid={`portfolio-edit-title-${p.file_id}`}
                  className="text-xs text-slate-600 hover:text-orange-600 w-full text-left truncate">
                  {p.titel || "+ Titel toevoegen"}
                </button>
              )}
              <Button data-testid={`portfolio-delete-${p.file_id}`} variant="outline" size="sm" onClick={() => remove(p.file_id)}
                className="w-full h-7 text-xs rounded-sm border-red-200 text-red-600 hover:bg-red-50">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Verwijderen
              </Button>
            </div>
          </div>
        ))}

        {photos.length < MAX && (
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
            data-testid="portfolio-upload-btn"
            className="aspect-square border-2 border-dashed border-slate-300 rounded-sm flex flex-col items-center justify-center gap-2 text-slate-400 hover:border-orange-400 hover:text-orange-500 transition-colors">
            {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
            <span className="text-xs font-semibold">{uploading ? "Uploaden…" : "Foto toevoegen"}</span>
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={onFile} data-testid="portfolio-file-input" />
    </div>
  );
}
